import React, { useEffect, useMemo, useState } from 'react';

import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { CellClassRules, ColDef } from 'ag-grid-community';
import { IBundlePricingRowData } from 'src/types/IBundlePricing';

import { generateGridErrorId } from '../../AddPrimeAMFRateCard/rateCardValidationReducer';
import { BaseAgGrid } from '../../BaseAgGrid';
import { reviewBundlePricingColDefs } from '../../BundlePricing/bundlePricingColDefs';
import '../AddBundlePricing.scss';

export interface IReviewBundlePricing {
    existingBundlePricingRowData: IBundlePricingRowData[];
    bundlePricingRowData: IBundlePricingRowData[];
}

export const ReviewBundlePricing: React.FC<IReviewBundlePricing> = ({
    existingBundlePricingRowData,
    bundlePricingRowData
}) => {
    const [displayedRowData, setDisplayedRowData] = useState<IBundlePricingRowData[]>([]);
    const [addedRows, setAddedRows] = useState(new Set<number>());
    const [deletedRows, setDeletedRows] = useState(new Set<number>());
    const [updatedCells, setUpdatedCells] = useState(new Set<string>());

    const recordsAdded = addedRows.size;
    const recordsDeleted = deletedRows.size;
    const recordsUpdated = useMemo(() => {
        const count = new Set<string>();
        updatedCells.forEach((row) => count.add(row.split('-*-')[0]));
        return count.size;
    }, [updatedCells]);

    const cellClassRules: CellClassRules = {
        'success-cell': (params) => addedRows.has(Number(params.node.id)),
        'error-cell': (params) => deletedRows.has(Number(params.node.id)),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        'warning-cell': (params) => updatedCells.has(generateGridErrorId(Number(params.node.id), params.colDef.field!))
    };
    const columnDefs: ColDef[] = [...reviewBundlePricingColDefs].map((colDef) => ({
        ...colDef,
        cellClassRules
    }));

    // Compute what rows are added/updated/deleted
    useEffect(() => {
        // Convert row data to map with key of territory#planName#subAttribute#startDate
        const convertRowDataToMap = (rowData: IBundlePricingRowData[]): Map<string, IBundlePricingRowData> =>
            new Map(
                rowData.map((row) => [
                    [row.territory, row.planName, row.planSubAttribute, row.startDate].join('#'),
                    row
                ])
            );

        const existingBundlePricingMap = convertRowDataToMap(existingBundlePricingRowData);
        const editedBundlePricingMap = convertRowDataToMap(bundlePricingRowData);

        const rowsAdded = new Set<number>();
        const rowsDeleted = new Set<number>();
        const cellsUpdated = new Set<string>();
        const rowsToBeDisplayed: IBundlePricingRowData[] = [];

        editedBundlePricingMap.forEach((row, key) => {
            const latestIndex = rowsToBeDisplayed.push(row) - 1;

            // Row exists in old and new row data
            if (existingBundlePricingMap.has(key)) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const existingRow = existingBundlePricingMap.get(key)!;

                // Check currency, price, end dates if matching
                const mismatchingFields = (['currency', 'price', 'endDate'] as (keyof IBundlePricingRowData)[]).filter(
                    (col) => row[col] !== existingRow[col]
                );
                mismatchingFields.forEach((field) => cellsUpdated.add(generateGridErrorId(latestIndex, field)));
            }

            // Row added
            else {
                rowsAdded.add(latestIndex);
            }
        });

        // Row data is deleted if contained in old row data but not new data
        existingBundlePricingMap.forEach((row, key) => {
            if (!editedBundlePricingMap.has(key)) {
                const latestIndex = rowsToBeDisplayed.push(row) - 1;
                rowsDeleted.add(latestIndex);
            }
        });

        setAddedRows(rowsAdded);
        setDeletedRows(rowsDeleted);
        setUpdatedCells(cellsUpdated);
        setDisplayedRowData(rowsToBeDisplayed);
    }, []);

    return (
        <div className="bundle-pricing-container">
            <Column className="bundle-pricing-content">
                <Heading level={2}>Review bundle pricing</Heading>
                <Column spacing="300" width="100%" height="100%" minHeight={480}>
                    <Row spacing="300">
                        <Box type="fill" spacingInset="300 400" data-testid="records-added">
                            <Row spacing="300">
                                <Text type="h100">{recordsAdded}</Text>
                                <Text>Records added</Text>
                            </Row>
                        </Box>
                        <Box type="fill" spacingInset="300 400" data-testid="records-updated">
                            <Row spacing="300">
                                <Text type="h100">{recordsUpdated}</Text>
                                <Text>Records updated</Text>
                            </Row>
                        </Box>
                        <Box type="fill" spacingInset="300 400" data-testid="records-deleted">
                            <Row spacing="300">
                                <Text type="h100">{recordsDeleted}</Text>
                                <Text>Records deleted</Text>
                            </Row>
                        </Box>
                    </Row>
                    <div className="bundle-pricing-grid">
                        <BaseAgGrid columnDefs={columnDefs} rowData={displayedRowData} />
                    </div>
                </Column>
            </Column>
        </div>
    );
};
